import {
    DefectSignature,
    DefectStatus,
    ObservationOutcome,
    VerificationResult,
} from '../types';

// FNP-904 — what KIND of defect is this, and how loudly should it read?
//
// ═══ WHY NOT_VERIFIED OUTRANKS FAIL ═══
//
// The ticket is explicit that these must not render identically, and the data is
// the reason. Every product bug the fleet has found (FNP-899 seat occupancy,
// FNP-900 profile save) arrived as NOT_VERIFIED — the action APPEARED to succeed
// and the platform silently failed to record its effect. Every FAIL so far has
// been harness or environment noise (a stale bundle, an exhausted catalog, an
// email-ingest outage).
//
// The schema says the same thing in its own words: NOT_VERIFIED is "the most
// valuable signal this platform produces". A UI that paints both red teaches an
// operator to skim past the one that matters.

export enum DefectKind {
    // PASS/anything + NOT_VERIFIED. The platform lied about success.
    UNVERIFIED_EFFECT = 'UNVERIFIED_EFFECT',
    // A hard failure — usually harness or environment.
    FAILURE = 'FAILURE',
    // Was resolved, then came back. See isRegression below.
    REGRESSION = 'REGRESSION',
    // Resolved and staying that way.
    RESOLVED = 'RESOLVED',
}

/**
 * A RESOLVED signature that has been seen again since it was resolved.
 *
 * The sweep never "unresolves" a row — `recordDefectOccurrence` uses
 * if_not_exists on status precisely so it cannot drag a human's triage decision
 * backwards. But it DOES advance lastSeenAt and the counters. So a resolved row
 * whose lastSeenAt is later than its resolvedAt is a defect that came back, and
 * saying so is this module's job: silently showing it as "Resolved" would be the
 * console telling a comfortable lie.
 */
export const isRegression = (record: Pick<DefectSignature, 'status' | 'lastSeenAt' | 'resolvedAt'>): boolean => {
    if (record.status !== DefectStatus.RESOLVED) {
        return false;
    }
    if (!record.resolvedAt || !record.lastSeenAt) {
        return false;
    }
    // ISO-8601 sorts lexicographically.
    return record.lastSeenAt > record.resolvedAt;
};

export const classifyDefect = (
    record: Pick<DefectSignature, 'status' | 'outcome' | 'verificationResult' | 'lastSeenAt' | 'resolvedAt'>,
): DefectKind => {
    if (isRegression(record)) {
        return DefectKind.REGRESSION;
    }
    if (record.status === DefectStatus.RESOLVED) {
        return DefectKind.RESOLVED;
    }
    // ⚠️ OUTCOME IS CHECKED FIRST, AND THAT ORDER IS LOAD-BEARING.
    //
    // The ticket frames this as "NOT_VERIFIED vs FAIL", which implies the two are
    // alternatives. Measured against 204 live dev observations, they are not:
    //
    //   ('FAIL',    'NOT_VERIFIED')     52   <-- every FAIL carries NOT_VERIFIED
    //   ('PASS',    'NOT_VERIFIED')      7   <-- the actual valuable signal
    //   ('PASS',    'VERIFIED')         15
    //   ('PASS',    'NOT_APPLICABLE')   18
    //   ('SKIPPED', 'NOT_APPLICABLE')  112
    //
    // The executor stamps NOT_VERIFIED on every failure — reasonably, since a
    // failed action verifies nothing. So testing verificationResult FIRST would
    // paint all 52 harness failures as "unverified effect" and bury the 7 rows
    // that are the real product-bug signal, producing exactly the flattening
    // this module exists to prevent.
    //
    // The valuable signal is specifically PASS + NOT_VERIFIED: the action
    // reported success and the platform did not record its effect. That is how
    // FNP-899 and FNP-900 presented.
    if (record.outcome === ObservationOutcome.FAIL) {
        return DefectKind.FAILURE;
    }
    if (record.verificationResult === VerificationResult.NOT_VERIFIED) {
        return DefectKind.UNVERIFIED_EFFECT;
    }
    return DefectKind.FAILURE;
};

export const defectKindDisplay = (kind: DefectKind): {
    color: string,
    label: string,
    description: string,
    muted: boolean,
} => {
    switch (kind) {
        case DefectKind.UNVERIFIED_EFFECT:
            return {
                color: 'orange',
                label: 'Unverified effect',
                description: 'The action appeared to succeed but the platform did not record its effect. This is the highest-value signal the fleet produces — every confirmed product bug so far arrived this way.',
                muted: false,
            };
        case DefectKind.REGRESSION:
            return {
                color: 'red',
                label: 'Regression',
                description: 'This defect was resolved and has since been seen again. It is NOT fixed.',
                muted: false,
            };
        case DefectKind.RESOLVED:
            return {
                color: 'green',
                label: 'Resolved',
                description: 'Has not recurred within the resolution window. Concluded by the sweep from an absence of occurrences, not by a human.',
                muted: true,
            };
        case DefectKind.FAILURE:
        default:
            return {
                color: 'red',
                label: 'Failure',
                description: 'A hard failure. Historically these have mostly been harness or environment problems rather than product defects.',
                muted: false,
            };
    }
};

export const defectStatusDisplay = (status: DefectStatus): { color: string, label: string } => {
    switch (status) {
        case DefectStatus.NEW:
            return {color: 'blue', label: 'New'};
        case DefectStatus.ACKNOWLEDGED:
            return {color: 'yellow', label: 'Acknowledged'};
        case DefectStatus.FILED:
            return {color: 'violet', label: 'Filed'};
        case DefectStatus.RESOLVED:
            return {color: 'green', label: 'Resolved'};
        default:
            return {color: 'gray', label: String(status)};
    }
};
